import React from 'react';
// import DualiteLogo from '~/assets/dualite-logo.svg';
// import AlphaLogo from '~/assets/alpha-logo.svg';

const OnboardingFooter: React.FC = () => {
  const scrollToId = (id: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(id);

    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Update the hash without jumping
      history.replaceState(null, '', `#${id}`);
    }
  };
  return (
    <>
      {/* Hero Section with Background Image */}
      <div className="relative w-full min-h-[400px] sm:min-h-[500px] lg:min-h-[700px] flex items-center justify-center bg-cover bg-center">
        {/* Background Image - Using img tag for better control and preloading */}
        <img
          src="/images/FooterBG.webp"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          aria-hidden="true"
          width="1920"
          height="700"
          decoding="async"
        />
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/20"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center gap-6 sm:gap-8 px-4">
          {/* Dualite Logo with Infinity Symbol
          <div className="flex items-center justify-center">
            <img 
              src={DualiteLogo} 
              alt="Dualite" 
              className="w-32 sm:w-40 lg:w-48 h-auto drop-shadow-2xl" 
            />
          </div> */}

          {/* Hero Text */}
          <h2 className="mt-[50%] text-white text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-semibold text-center leading-tight drop-shadow-lg">
            Build great apps with Dualite
          </h2>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full bg-[#f5f5f5] border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-6 sm:gap-8 lg:gap-6">
            {/* Brand Column */}
            <div className="col-span-2 sm:col-span-3 lg:col-span-2">
              <div className="mb-3 flex items-center gap-2 text-black">
                <img
                  src="/images/dualite-logo.svg"
                  alt="Dualite"
                  className="h-4 w-auto"
                  style={{ filter: 'brightness(0) saturate(100%)' }}
                />
                <img
                  src="/images/alpha-logo.svg"
                  alt="Dualite"
                  className="h-4 w-auto"
                  style={{ filter: 'brightness(0) saturate(100%)' }}
                />
              </div>
              <p className="text-[#10110C]/70 text-sm leading-relaxed">Turn your idea into reality with Dualite</p>
            </div>

            {/* Product Column */}
            <div>
              <h3 className="text-[#10110C] font-semibold text-sm mb-3 sm:mb-4">Product</h3>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a
                    href="#features"
                    onClick={scrollToId('features')}
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="#pricing"
                    onClick={scrollToId('pricing')}
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Pricing
                  </a>
                </li>
                <li>
                  <a
                    href="#faqs"
                    onClick={scrollToId('faqs')}
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    FAQs
                  </a>
                </li>
                <li>
                  <a
                    href="https://dualite.dev/changelog"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Changelog
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h3 className="text-[#10110C] font-semibold text-sm mb-3 sm:mb-4">Resources</h3>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a
                    href="https://alphadocs.dualite.dev"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Documentation
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.youtube.com/@dualitealpha"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Videos
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:support@dualite.dev"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Support
                  </a>
                </li>
                <li>
                  <a
                    href="https://dualite.dev/showcase"
                    target="_blank"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Showcase
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h3 className="text-[#10110C] font-semibold text-sm mb-3 sm:mb-4">Company</h3>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a
                    href="https://dualite.dev/blog"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Blog
                  </a>
                </li>
                <li>
                  <a
                    href="https://coral-muse-2d3.notion.site/Dualite-Affiliate-Program-2a169182fdee8024b84cf1dfef6f2db5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Affiliates
                  </a>
                </li>
              </ul>
            </div>

            {/* Socials Column */}
            <div>
              <h3 className="text-[#10110C] font-semibold text-sm mb-3 sm:mb-4">Socials</h3>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a
                    href="https://x.com/dualitedev"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    X/Twitter
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.linkedin.com/company/dualitedev"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.instagram.com/dualite.dev"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Instagram
                  </a>
                </li>
                <li>
                  <a
                    href="https://discord.gg/vCXsN3hYpf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Discord
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div>
              <h3 className="text-[#10110C] font-semibold text-sm mb-3 sm:mb-4">Legal</h3>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a
                    href="https://dualite.dev/legal/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:support@dualite.dev"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Contact for Support
                  </a>
                </li>
                <li>
                  <a
                    href="https://dualite.dev/legal/cancellation-and-refund-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Cancellation and Refund Policy
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:abuse@dualite.dev"
                    className="text-[#10110C]/70 hover:text-[#10110C] text-sm transition-colors"
                  >
                    Report Abuse
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <p className="text-center text-[#10110C]/60 text-xs sm:text-sm">© 2025 Dualite. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default OnboardingFooter;
